import React, { useState, useEffect } from "react";
import { authHeaders } from "../../utils/auth";
import AccuracyGraph from "../../components/AccuracyGraph";
import { PatientSidebar } from "../../components/DashBoard/patientSidebar";
import FiltersBar, {
  handQueryValue,
  hasRequiredFilters as filtersReady,
} from "../../components/DashBoard/FiltersBar";
import ResultsSection from "../../components/DashBoard/ResultsSection";
import { jwtDecode } from "jwt-decode";
import ActivitiesSelector from "../../components/DashBoard/ActivitiesSelector";
import { Link } from "react-router-dom";
import styles from "./dashboard.module.css";
import {
  PREVIEW_PROVIDER_DEMO_PATIENTS,
  filterPreviewEntries,
  toGraphEntries,
} from "../../data/dashboardPreviewSample";

const baseURL = process.env.REACT_APP_BACKEND_API_URL;

const categories: Record<string, string[]> = {
  Bowl: ["Vertical Bowl", "Horizontal Bowl"],
  Mug: [
    "Vertical Mug",
    "Horizontal Mug",
    "Sip from Mug",
    "Quick Test Mug",
    "Slow Pour Mug",
  ],
  Other: ["Phone Number", "Quick Tap"],
};

type Props = { previewMode?: boolean };

export default function Dashboard({ previewMode = false }: Props) {
  const [entries, setEntries] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filterHand, setFilterHand] = useState(previewMode ? "Right" : "");
  const [filterExercise, setFilterExercise] = useState(
    previewMode ? "Vertical Mug" : ""
  );
  const [filterStartDate, setStartDate] = useState("");
  const [filterEndDate, setEndDate] = useState("");
  const [name, setName] = useState(previewMode ? "Dr. Chen" : "");
  const [selectedPatient, setSelectedPatient] = useState(
    previewMode ? PREVIEW_PROVIDER_DEMO_PATIENTS[0].email : ""
  );
  const [datakey, setDataKey] = useState("Accuracy");
  const hasRequiredFilters = filtersReady(filterHand, filterExercise);

  const handleSignOut = () => {
    localStorage.removeItem("idToken");
    localStorage.removeItem("accessToken");
    window.location.href = "/login";
  };

  useEffect(() => {
    if (previewMode) return;

    const idToken = localStorage.getItem("idToken");
    if (idToken) {
      const user = JSON.parse(atob(idToken.split(".")[1]));
      const displayName = user.given_name || user.name || "User";
      const decoded: any = jwtDecode(idToken);
      const groups = decoded["cognito:groups"] || [];

      if (groups.includes("provider") || groups.includes("Provider")) {
        setName(displayName);
      } else {
        handleSignOut();
      }
    } else {
      window.location.href = "/login";
    }
  }, [previewMode]);

  useEffect(() => {
    if (!selectedPatient) return;
    if (!hasRequiredFilters) {
      setEntries([]);
      setError(null);
      return;
    }

    if (previewMode) {
      const rows = filterPreviewEntries(
        selectedPatient,
        filterHand,
        filterExercise
      );
      setEntries(toGraphEntries(rows));
      setError(null);
      return;
    }

    const fetchEntries = async () => {
      try {
        setError(null);
        const params = new URLSearchParams();
        params.append("username", selectedPatient);
        params.append("hand", handQueryValue(filterHand, filterExercise));
        params.append("exerciseName", filterExercise);
        if (filterStartDate) params.append("start", filterStartDate);
        if (filterEndDate) params.append("end", filterEndDate);

        const res = await fetch(`${baseURL}api/aws/filtered?${params.toString()}`, { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Unknown error");
        setEntries(data.entries);
      } catch (err: any) {
        console.error("❌ Error fetching entries:", err);
        setError(err.message || "Failed to fetch entries.");
      }
    };
    fetchEntries();
  }, [
    selectedPatient,
    filterHand,
    filterExercise,
    filterStartDate,
    filterEndDate,
    hasRequiredFilters,
    previewMode,
  ]);

  return (
    <div className={styles.dashLayout}>
      {previewMode ? (
        <aside className={styles.dashSidebar}>
          <div className={styles.previewSidebarInner}>
            <p className={styles.previewSidebarTitle}>Patients (sample)</p>
            <ul className={styles.previewPatientList}>
              {PREVIEW_PROVIDER_DEMO_PATIENTS.map((p) => (
                <li key={p.email}>
                  <button
                    type="button"
                    className={
                      selectedPatient === p.email
                        ? styles.previewPatientBtnActive
                        : styles.previewPatientBtn
                    }
                    onClick={() => setSelectedPatient(p.email)}
                  >
                    {p.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      ) : (
        <aside className={styles.dashSidebar}>
          <PatientSidebar
            selectedPatient={selectedPatient}
            onSelectPatient={setSelectedPatient}
          />
        </aside>
      )}

      <main className={styles.dashContent}>
        {previewMode ? (
          <p className="ui-banner ui-banner--brand" role="status">
            Preview with sample data only.{" "}
            <Link to="/login" className="ui-link">
              Sign in
            </Link>{" "}
            as a provider to connect to real patients.
          </p>
        ) : null}

        {!selectedPatient ? (
          <div className={styles.welcome}>
            <h1 className={styles.welcomeTitle}>Welcome {name}</h1>
            <p className={styles.note}>
              Select a patient in the sidebar to begin.
            </p>
          </div>
        ) : null}

        {selectedPatient && (
          <>
            <FiltersBar
              filterHand={filterHand}
              filterExercise={filterExercise}
              filterStartDate={filterStartDate}
              filterEndDate={filterEndDate}
              dataKey={datakey}
              setFilterHand={setFilterHand}
              setFilterExercise={setFilterExercise}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              setDataKey={setDataKey}
            />

            {error && (
              <p className="ui-banner ui-banner--error" role="alert">
                {error}
              </p>
            )}

            {!hasRequiredFilters ? (
              <p className={styles.note}>
                Select a hand and an exercise to view results.
              </p>
            ) : null}

            {entries.length > 0 && <AccuracyGraph data={entries} dataKey={datakey} />}

            {entries.length > 0 ? (
              <ResultsSection entries={entries} selectedPatient={selectedPatient} />
            ) : hasRequiredFilters ? (
              <p className={styles.note}>No entries found for this patient.</p>
            ) : null}
            {!previewMode ? (
              <ActivitiesSelector
                patientEmail={selectedPatient}
                categories={categories}
              />
            ) : null}
          </>
        )}
      </main>
    </div>
  );
}
